import React, { useEffect, useRef } from 'react';
import { PercentInput } from '../percent-input/PercentInput';
import { CheckoutStatusDots } from '../checkout-status/CheckoutStatusDots';
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal';
import { Checkbox } from '../checkbox/Checkbox';
import {
  TrafficAllocationVariant,
  TrafficAllocationConfig,
  TrafficAllocationFormData,
} from './TrafficAllocationShared';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';

export interface TrafficAllocationInputProps {
  trafficAllocationConfig: TrafficAllocationConfig;
  onChange: (valid: boolean, variants: TrafficAllocationVariant[]) => void;
  viewMode?: boolean;
  showValidation?: boolean;
  shop?: Shop;
}

export const TrafficAllocationInput: React.FC<TrafficAllocationInputProps> = ({
  trafficAllocationConfig,
  onChange,
  viewMode = false,
  showValidation,
  shop,
}) => {
  const [validationMessageForModal, setValidationMessageForModal] = React.useState<string>('');
  const [formData, setFormData] = React.useState<TrafficAllocationFormData>({
    variants: trafficAllocationConfig.variants.map((variant, idx) => {
      return {
        ...variant,
        internalId: idx,
      };
    }),
  });
  const prevDataRef = useRef<{ isValid: boolean; variants: TrafficAllocationVariant[] }>();

  useEffect(() => {
    const percentageSum = formData.variants
      .filter((x) => x.isSelected)
      .reduce((total, obj) => {
        return total + obj.percentageTraffic;
      }, 0);

    const isValid = percentageSum === 100;

    // Only call onChange if values actually changed to prevent infinite loop
    const prevData = prevDataRef.current;
    const hasChanged =
      !prevData ||
      prevData.isValid !== isValid ||
      JSON.stringify(prevData.variants) !== JSON.stringify(formData.variants);

    if (hasChanged) {
      onChange(isValid, formData.variants);
      prevDataRef.current = { isValid, variants: formData.variants };
    }
  }, [formData, onChange]);

  const setVariantPercentage = (id: number, percentage: number) => {
    setFormData((oldFormData) => {
      return {
        variants: oldFormData.variants.map((variant) => {
          if (variant.internalId === id) {
            return {
              ...variant,
              percentageTraffic: percentage,
            };
          }
          return variant;
        }),
      };
    });
  };

  const toggleSelected = (id: number) => {
    setFormData((prevData) => {
      const clickedVariant = prevData.variants.find((v) => v.internalId === id);
      if (!clickedVariant) return prevData;

      const currentSelectedCount = prevData.variants.filter((v) => v.isSelected).length;
      const maxVariants = 5;

      // Prevent selecting more than max allowed
      if (!clickedVariant.isSelected && currentSelectedCount === maxVariants) {
        setValidationMessageForModal(`Currently we only support ${maxVariants} variants for testing.`);
        return prevData;
      }

      const newSelectedCount = clickedVariant.isSelected ? currentSelectedCount - 1 : currentSelectedCount + 1;

      // For 1-2 variants, use 50% default. For 3+, distribute evenly
      const basePercentage = newSelectedCount < 3 ? 50 : Math.floor(100 / newSelectedCount);
      const remainder = newSelectedCount < 3 ? 0 : 100 - basePercentage * newSelectedCount;

      let assignedCount = 0;
      const totalToAssign = newSelectedCount;

      return {
        variants: prevData.variants.map((variant) => {
          // Toggle the clicked variant
          if (variant.internalId === id) {
            const isLastToAssign = assignedCount === totalToAssign - 1;
            const percentage = variant.isSelected
              ? 0
              : isLastToAssign && newSelectedCount >= 3
                ? basePercentage + remainder
                : basePercentage;
            if (!variant.isSelected) assignedCount++;
            return {
              ...variant,
              isSelected: !variant.isSelected,
              percentageTraffic: percentage,
            };
          }
          // Update all other selected variants with new percentage distribution
          // This handles both: adding a 3rd variant (2->3) and removing to go back to 2 (3->2)
          if (variant.isSelected) {
            const isLastToAssign = assignedCount === totalToAssign - 1;
            const percentage = isLastToAssign && newSelectedCount >= 3 ? basePercentage + remainder : basePercentage;
            assignedCount++;
            return { ...variant, percentageTraffic: percentage };
          }
          return variant;
        }),
      };
    });
  };

  return (
    <div className="traffic-allocation-input">
      {formData.variants.map((variant) => (
        <div
          key={variant.internalId}
          className={`traffic-allocation-input__item ${!viewMode && 'traffic-allocation-input__clickable'} ${variant.isSelected && 'traffic-allocation-input__item__selected'}`}
          onMouseDown={() => {
            if (!viewMode) {
              toggleSelected(variant.internalId);
            }
          }}>
          <div>
            <Checkbox checked={variant.isSelected} onChange={() => {}} disabled={viewMode} />
          </div>
          <div className="traffic-allocation-input__item__info">
            <div className={`traffic-allocation-input__item__info__title ${viewMode && 'disabled'}`}>
              {variant.name}
            </div>
            <div className={`traffic-allocation-input__item__info__description ${viewMode && 'disabled'}`}>
              {variant.description}
            </div>
          </div>
          <div className="traffic-allocation-input__item__status">
            <CheckoutStatusDots status={variant.checkoutStatus} showTooltip={false} />
          </div>
          <div className="traffic-allocation-input__item__percentage">
            <PercentInput
              value={variant.percentageTraffic}
              setValue={(value: number) => {
                if (!viewMode) setVariantPercentage(variant.internalId, value);
              }}
              disabled={viewMode || (variant.isSelected ? false : true)}
              setValid={() => {}}
              viewMode={viewMode}
            />
          </div>
        </div>
      ))}
      {validationMessageForModal && (
        <ConfirmationModal
          title={validationMessageForModal}
          buttonText={'Ok'}
          isOpen={validationMessageForModal.length > 0}
          mainContent={'Please unselect one of your variants to select this one.'}
          onClose={() => {
            setValidationMessageForModal('');
          }}
          onSuccess={() => {
            setValidationMessageForModal('');
          }}
          hideCancel={true}
        />
      )}
    </div>
  );
};
